/** Fields extracted from a SQL connection string. */
export interface ParsedSqlConnectionUrl {
  host: string;
  port: string;
  database: string;
  user: string;
  password: string;
  ssl: boolean;
}

const ACCEPTED_SCHEMES = new Set([
  "postgres",
  "postgresql",
  "mysql",
  "redshift",
]);

/**
 * Parses a SQL connection string (Postgres, MySQL, Redshift) into the flat
 * datasource form fields. Returns `null` when the input isn't a recognizable
 * connection URL so callers can leave the form untouched.
 *
 * Fields absent from the URL come back as empty strings; the caller decides
 * whether to fall back to a driver default (e.g. the port).
 */
export const parseSqlConnectionUrl = (
  raw: string,
): ParsedSqlConnectionUrl | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Tolerate a leading `jdbc:` prefix (e.g. `jdbc:postgresql://...`).
  const withoutJdbc = trimmed.startsWith("jdbc:")
    ? trimmed.slice("jdbc:".length)
    : trimmed;

  let url: URL;
  try {
    url = new URL(withoutJdbc);
  } catch {
    return null;
  }

  const scheme = url.protocol.replace(/:$/, "").toLowerCase();
  if (!ACCEPTED_SCHEMES.has(scheme)) return null;
  if (!url.hostname) return null;

  const database = decodeURIComponent(url.pathname.replace(/^\//, "")).split(
    "/",
  )[0];

  return {
    host: url.hostname,
    port: url.port,
    database: database ?? "",
    user: url.username ? decodeURIComponent(url.username) : "",
    password: url.password ? decodeURIComponent(url.password) : "",
    ssl: detectSsl(url.searchParams),
  };
};

/**
 * Determines whether SSL should be enabled from a connection string's query
 * params. `sslmode` values other than `disable`/`allow` count as SSL, as does
 * an explicit `ssl=true`.
 */
const detectSsl = (params: URLSearchParams): boolean => {
  const sslMode = params.get("sslmode")?.toLowerCase();
  if (sslMode && sslMode !== "disable" && sslMode !== "allow") return true;

  const ssl = params.get("ssl")?.toLowerCase();
  return ssl === "true" || ssl === "1" || ssl === "require";
};
